import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    HostBinding,
    OnDestroy,
    OnInit,
    ChangeDetectorRef
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UxAppShellService, UxLanguage, UxEuLanguages } from '@eui/core';
import { distinctUntilKeyChanged, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { BaseDirective } from '../../base.directive';
import { EuiDialogComponent, EuiDialogConfig, EuiDialogService } from '../../components/eui-dialog';
import { EuiModalSelectorComponent } from './modal-selector/modal-selector.component';
import { EuiDynamicComponentService } from '../../services';

@Component({
    selector: 'eui-language-selector',
    templateUrl: './language-selector.component.html',
    styleUrls: ['./_styles/_index.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None,
    providers: [EuiDynamicComponentService],
})

export class EuiLanguageSelectorComponent extends BaseDirective implements OnInit, OnDestroy {
    @HostBinding('class')
    get cssClasses() {
        return [
            this.languages?.length === 0 ? `eui-language-selector--empty` : ``,
            super.getCssClasses('eui-language-selector'),
        ].join(' ').trim();
    }

    languages: UxLanguage[];
    selectedLanguage: UxLanguage;
    private dialogRef: MatDialogRef<EuiDialogComponent>;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private cd: ChangeDetectorRef,
        private euiDialogService: EuiDialogService,
        public appShellService: UxAppShellService,
        private translateService: TranslateService,
    ) {
        super();
    }

    /**
     * returns true if there are at least one and max four languages.
     */
    get isShowDropDown(): boolean {
        return this.languages?.length > 1 && this.languages?.length < 4;
    }

    /**
     * returns true if there are at least five languages.
     */
    get isShowModal(): boolean {
        return this.languages?.length >= 4;
    }

    ngOnInit() {
        // update languages array based on AppShell State 'language' changes
        this.appShellService.state$.pipe(
            takeUntil(this.destroy$),
            distinctUntilKeyChanged('languages'),
            map(state => state.languages)
        ).subscribe((languages) => {
            this.languages = (languages) ?
                UxEuLanguages.getOrderedLanguages(languages) :
                UxEuLanguages.getLanguages();
            this.cd.detectChanges();
        });

        // update activeLanguage array based on AppShell State 'language' changes
        this.appShellService.state$.pipe(
            takeUntil(this.destroy$),
            distinctUntilKeyChanged('activeLanguage'),
            map(state => state.activeLanguage)
        ).subscribe((activeLanguage) => {
            this.selectedLanguage = this.getLanguage(activeLanguage);
            this.cd.detectChanges();
        });
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.dialogRef?.close();
    }

    onLanguageChanged(language: UxLanguage) {
        // Update the AppShellService state
        this.appShellService.setState({
            ...this.appShellService.state,
            activeLanguage: language.code,
        });
    }

    /**
     * Opens the modal that renders the UxLanguage array with modal actions bindings to this Component
     */
    open(titleLabel: string = this.translateService.instant('eui.languageSelector.modalTitle')) {
        this.dialogRef = this.euiDialogService.openDialog(new EuiDialogConfig({
            title: titleLabel,
            isHandleCloseOnClickOutside: true,
            hasFooter: false,
            onClickOutside: (dialogRef: MatDialogRef<any, any>) => {
                this.euiDialogService.closeDialog(dialogRef);
            },
            bodyComponent: {
                component: EuiModalSelectorComponent,
                config: {
                    languages: this.languages,
                    selectedLanguage: this.selectedLanguage,
                    languageChanged: (language: UxLanguage) => this.onLanguageChanged(language),
                },
            },
        }));
    }

    /**
     * returns the UxLanguage Object from a given language code
     * @param languageCode two char language code
     * @protected
     */
    protected getLanguage(languageCode: string): UxLanguage {
        return this.languages.find(lang => lang.code === languageCode);
    }
}
